/**
 * Asset Service for Poker Reborn
 *
 * Handles loading and caching of game assets using PixiJS.
 */

import { Assets, Texture, Spritesheet } from 'pixi.js';
import { EventEmitter } from './EventEmitter';
import { ASSET_GROUPS, getCardAtlasPath, getCardBackPath } from '@/config/assets';
import { log } from '@/utils/log';

// =============================================================================
// TYPES
// =============================================================================

/** Asset loading progress */
export interface LoadProgress {
  loaded: number;
  total: number;
  percent: number;
}

/** Asset service events */
export interface AssetEvents {
  /** Loading started */
  loadStart: { group: string };

  /** Loading progress */
  progress: LoadProgress;

  /** Loading complete */
  loadComplete: { group: string };

  /** Loading error */
  error: { group: string; error: Error };
}

// =============================================================================
// ASSET SERVICE
// =============================================================================

export class AssetService extends EventEmitter<AssetEvents> {
  private static instance: AssetService | null = null;

  private loadedGroups: Set<string> = new Set();
  private loading: Map<string, Promise<void>> = new Map();

  private constructor() {
    super();
  }

  /** Get singleton instance */
  static getInstance(): AssetService {
    if (!AssetService.instance) {
      AssetService.instance = new AssetService();
    }
    return AssetService.instance;
  }

  /**
   * Initialize the asset service
   */
  async init(): Promise<void> {
    // Initialize PixiJS Assets
    await Assets.init();
  }

  /**
   * Check if an asset group is loaded
   */
  isGroupLoaded(groupName: string): boolean {
    return this.loadedGroups.has(groupName);
  }

  /**
   * Load an asset group
   */
  async loadGroup(groupName: keyof typeof ASSET_GROUPS): Promise<void> {
    // Already loaded
    if (this.loadedGroups.has(groupName)) {
      return;
    }

    // Already loading
    if (this.loading.has(groupName)) {
      return this.loading.get(groupName);
    }

    const group = ASSET_GROUPS[groupName];
    if (!group) {
      throw new Error(`Unknown asset group: ${groupName}`);
    }

    // Build asset list — inject resolution-aware card assets for the table group
    let assets = [...group.assets];
    if (groupName === 'table') {
      const cardAtlasPath = getCardAtlasPath();
      const cardBackPath = getCardBackPath();
      assets = [cardAtlasPath, cardBackPath, ...assets];
      log.assets.info`Loading card assets: atlas=${cardAtlasPath}, back=${cardBackPath}`;
    }

    const loadPromise = this.doLoadGroup(groupName, assets);
    this.loading.set(groupName, loadPromise);

    try {
      await loadPromise;
      this.loadedGroups.add(groupName);
    } finally {
      this.loading.delete(groupName);
    }
  }

  /**
   * Load multiple asset groups
   */
  async loadGroups(groupNames: (keyof typeof ASSET_GROUPS)[]): Promise<void> {
    await Promise.all(groupNames.map((name) => this.loadGroup(name)));
  }

  /**
   * Load boot assets (minimum for loading screen)
   */
  async loadBoot(): Promise<void> {
    await this.loadGroup('boot');
  }

  /**
   * Load core assets (needed for lobby)
   */
  async loadCore(): Promise<void> {
    await this.loadGroup('core');
  }

  /**
   * Load table assets (needed for game)
   */
  async loadTable(): Promise<void> {
    await this.loadGroup('table');
  }

  /**
   * Get a loaded texture
   */
  getTexture(name: string): Texture {
    const texture = Assets.get<Texture>(name);
    if (!texture) {
      log.assets.warning`Texture not found: ${name}`;
      return Texture.EMPTY;
    }
    return texture;
  }

  /**
   * Get a sprite from an atlas
   */
  getSprite(atlasName: string, spriteName: string): Texture {
    const atlas = Assets.get<Spritesheet>(atlasName);
    if (!atlas) {
      log.assets.warning`Atlas not found: ${atlasName}`;
      return Texture.EMPTY;
    }

    const texture = atlas.textures[spriteName];
    if (!texture) {
      log.assets.warning`Sprite not found: ${spriteName} in ${atlasName}`;
      return Texture.EMPTY;
    }

    return texture;
  }

  /**
   * Get a card face texture from the loaded atlas
   */
  getCardTexture(rank: string, suit: string): Texture {
    return this.getSprite(getCardAtlasPath(), `card_${rank}${suit}`);
  }

  /**
   * Get the card back texture (resolution-appropriate, loaded at init)
   */
  getCardBackTexture(): Texture {
    const cardBackPath = getCardBackPath();
    return this.getTexture(cardBackPath);
  }

  /**
   * Get a chip texture — use ChipColors.getChipTexture() instead.
   * Chip textures are loaded from the atlas via ChipColors module.
   */

  /**
   * Unload an asset group
   */
  async unloadGroup(groupName: string): Promise<void> {
    if (!this.loadedGroups.has(groupName)) {
      return;
    }

    const group = ASSET_GROUPS[groupName as keyof typeof ASSET_GROUPS];
    if (group) {
      let assets = [...group.assets];
      // Include dynamic card assets for the table group
      if (groupName === 'table') {
        assets = [getCardAtlasPath(), getCardBackPath(), ...assets];
      }
      await Assets.unload(assets);
    }

    this.loadedGroups.delete(groupName);
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private async doLoadGroup(groupName: string, assets: string[]): Promise<void> {
    this.emit('loadStart', { group: groupName });

    try {
      // Add assets to loader
      for (const asset of assets) {
        Assets.add({ alias: asset, src: asset });
      }

      // Load with progress
      let loaded = 0;
      const total = assets.length;

      await Assets.load(assets, (progress) => {
        loaded = Math.floor(progress * total);
        this.emit('progress', {
          loaded,
          total,
          percent: progress * 100,
        });
      });

      this.emit('loadComplete', { group: groupName });
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.emit('error', { group: groupName, error: err });
      throw err;
    }
  }
}

/** Singleton asset service instance */
export const assetService = AssetService.getInstance();

// =============================================================================
// CARD TEXTURE PRELOADING
// Follows the same pattern as preloadChipTextures() in ChipColors.ts
// =============================================================================

let cardTexturesLoaded = false;

/**
 * Preload card sprite atlas and card back image.
 * Picks 1x or 2x based on devicePixelRatio.
 * Call this during app initialization (before CardViews are created).
 */
export async function preloadCardTextures(): Promise<void> {
  if (cardTexturesLoaded) return;

  const cardAtlasPath = getCardAtlasPath();
  const cardBackPath = getCardBackPath();

  await Assets.load([cardAtlasPath, cardBackPath]);
  cardTexturesLoaded = true;

  log.assets.info`Card textures loaded: atlas=${cardAtlasPath}, back=${cardBackPath}`;
}
